/**
 * Kingdom Factor - Statement of Faith
 * Simple content page displaying beliefs and practices with scripture references
 */

import { SeoHead } from "@/components/SeoHead"
import { BookOpen } from "lucide-react"
import { Header } from "@/components/layout/Header"
import { Footer } from "@/components/layout/Footer"

export default function StatementOfFaith({ seo, settings = {} }) {
  const { scheduleCallUrl = "#" } = settings

  const beliefs = [
    {
      title: "The Bible",
      content:
        "We believe that the Bible is God's written revelation to mankind, and that it is verbally inspired, authoritative and without error in the original manuscripts.",
    },
    {
      title: "Jesus Christ",
      content:
        "We believe in the deity of Jesus Christ, His virgin birth, sinless life, miracles, death on the cross to provide for our redemption, bodily resurrection, and ascension into heaven, present ministry of intercession for us, and His future return to earth in power and glory.",
    },
    {
      title: "Mankind & Salvation",
      content:
        "We believe that mankind was created in the image of God, but because of sin, was alienated from God. Only through faith, trusting in Christ alone for salvation, which was made possible by His death and resurrection, can that alienation be removed.",
    },
  ]

  const practices = [
    {
      title: "Member Value",
      content:
        "We practice the belief that each member has value and that each member becomes more valuable when they are a part of Kingdom Cohort.",
      verse: "Where there are many advisors, there is much success.",
      reference: "Proverbs 15:22",
    },
    {
      title: "KF Peer Groups",
      content:
        "We practice the belief that KF Peer Groups challenge and encourage Christian business leaders to build the Kingdom of God in the marketplace through studying together, sharing insights with each other, holding each other accountable, and praying for each other persistently.",
      verse:
        "Therefore confess your sins to each other and pray for each other so that you may be healed. The prayer of a righteous person is powerful and effective.",
      reference: "James 5:16",
    },
    {
      title: "Marketplace Missionaries",
      content:
        "We practice the belief that every Christian business leader can become a marketplace missionary and impact the Kingdom by building their business on biblical business principles.",
      verse:
        "About five in the afternoon he went out and found still others standing around. He asked them, 'Why have you been standing here all day long doing nothing?' 'Because no one has hired us,' they answered. He said to them, 'You also go and work in my vineyard.'",
      reference: "Matthew 20:5-6",
    },
    {
      title: "Well Done",
      content:
        "We practice the belief that success for the Christian leader is hearing the words Well Done pertaining to life and business at the end of their life and all throughout their life.",
      verse:
        "His master replied, 'Well done, good and faithful servant! You have been faithful with a few things; I will put you in charge of many things. Come and share your master's happiness!'",
      reference: "Matthew 25:21",
    },
  ]

  return (
    <>
      <SeoHead seo={seo} />

      <div className="bg-white text-zinc-900 min-h-screen">
        <Header activePage="statement-of-faith" scheduleCallUrl={scheduleCallUrl} />

        {/* Hero / Header */}
        <section className="bg-zinc-50 pt-32 pb-16">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-kf-gradient rounded-full mb-6">
              <BookOpen className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-display text-4xl md:text-5xl text-zinc-900 mb-4">
              STATEMENT OF PRACTICE
            </h1>
            <p className="text-xl text-zinc-600">
              Connecting Leaders so They May Finish Well
            </p>
          </div>
        </section>

        {/* What We Believe */}
        <section className="bg-white py-20">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <h2 className="font-display text-2xl md:text-3xl text-zinc-900 mb-10 text-center">
              WHAT WE <span className="text-kf-red">BELIEVE</span> AT KINGDOM
              FACTOR
            </h2>

            <div className="space-y-6">
              {beliefs.map((belief, index) => (
                <div
                  key={index}
                  className="bg-zinc-50 border border-zinc-200 rounded-lg p-6"
                >
                  <h3 className="font-display text-lg text-kf-red mb-3">
                    {belief.title}
                  </h3>
                  <p className="text-zinc-700 leading-relaxed">
                    {belief.content}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What We Practice */}
        <section className="bg-zinc-50 py-20">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <h2 className="font-display text-2xl md:text-3xl text-zinc-900 mb-10 text-center">
              WHAT WE <span className="text-kf-red">PRACTICE</span> AT KINGDOM
              FACTOR
            </h2>

            <div className="space-y-8">
              {practices.map((practice, index) => (
                <div
                  key={index}
                  className="bg-white border border-zinc-200 rounded-lg p-6"
                >
                  <h3 className="font-display text-lg text-kf-red mb-3">
                    {practice.title}
                  </h3>
                  <p className="text-zinc-700 leading-relaxed mb-4">
                    {practice.content}
                  </p>
                  <blockquote className="border-l-4 border-kf-red pl-4 py-2 bg-zinc-50 rounded-r">
                    <p className="text-zinc-600 italic mb-2">
                      "{practice.verse}"
                    </p>
                    <cite className="text-sm text-kf-red font-semibold not-italic">
                      — {practice.reference}
                    </cite>
                  </blockquote>
                </div>
              ))}
            </div>
          </div>
        </section>

        <Footer activePage="statement-of-faith" />
      </div>
    </>
  )
}
